import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from "@angular/core";
import { ButtonComponent } from "@corp-ais/omniark-ui";

/**
 * Contextual save bar for the per-section save cutover — the
 * Shopify-admin/Discord-settings pattern: NOTHING renders while the active
 * tab is clean; the bar slides up from the bottom edge the moment an edit
 * lands, naming the tab it will save ("Save Pricing"). Because it appears
 * in response to the user's own edit and carries the tab's name, it never
 * reads as a page-wide Save — which every permanently-placed variant did.
 *
 * Second mode — save-to-stage: when the tab is CLEAN but the selected price
 * is not yet staged (`stageStatus` set: "In design" / "Rejected"), the bar
 * appears with a "Save to stage" action instead. The BE advances a price to
 * Designed on any per-price PATCH, and a clean price has nothing to edit —
 * this mode is its only save path. Dirty mode always wins: with edits
 * pending, a normal Save stages the price anyway.
 *
 * Purely presentational: the shell decides WHAT is dirty/stageable and
 * WHICH coordinator half to call — this bar only forwards `save` /
 * `discard` / `stage`.
 *
 * Uses `enr-button` like the hero's actions. In Jest the omniark mock
 * (`src/setup-jest.ts`) renders it empty with no `clicked` output, so the
 * spec triggers the output via `triggerEventHandler("clicked")` — the same
 * pattern ussd-v2's specs use.
 */
@Component({
  selector: "app-section-save-bar",
  standalone: true,
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./section-save-bar.html",
  styleUrl: "./section-save-bar.scss",
})
export class SectionSaveBar {
  readonly dirty = input(false);
  readonly saving = input(false);

  /** Short label of the tab this bar saves (e.g. "Pricing") — shown in the
   *  hint and on the Save button so the scope is explicit. */
  readonly tabLabel = input("");

  /** Status label ("In design" / "Rejected") of the CLEAN selected price
   *  when it can be staged by saving as-is; null hides the stage mode. */
  readonly stageStatus = input<string | null>(null);

  /** Why the shell refuses to save right now (a closed package, no
   *  permission, a role that moved), or null. Both save actions are
   *  disabled and the reason is shown: a button that only ever 403s is
   *  worse than one that explains itself. Discard stays live, because
   *  abandoning local edits is the one thing a user who cannot save still
   *  needs to do. */
  readonly lockReason = input<string | null>(null);

  readonly save = output<void>();
  readonly discard = output<void>();
  readonly stage = output<void>();

  protected onSaveClick(): void {
    if (!this.dirty() || this.saving() || this.lockReason()) return;
    this.save.emit();
  }

  protected onDiscardClick(): void {
    if (!this.dirty() || this.saving()) return;
    this.discard.emit();
  }

  protected onStageClick(): void {
    if (this.saving() || this.lockReason()) return;
    this.stage.emit();
  }
}
